import React, { useEffect, useState } from 'react';
import {
	Button,
	Col,
	Input,
	Row,
	Label,
	ModalBody,
	Form,
	Spinner,
	FormFeedback,
	FormGroup,
	Nav,
	NavItem,
	NavLink,
	TabContent,
	TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import OSelect from '../../../../Components/OElements/OSelect';
import OHr from '../../../../Components/OElements/OHr';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import {
	addNewPowerGenerationSource,
	updatePowerGenerationSource,
} from '../../../../slices/powerGenerationSource/thunk';
import { getPowerGenerationRegisterStatusCode } from '../../../../slices/powerGenerationRegisterStatus/thunk';
import {
	getEstablishmentList,
	getEstablishmentTypeList,
	getFeedingBargesList,
	getFuelTankList,
	getFuelTankListByEstablishment,
	getFuelTypeList,
	getFuelTypeUnitMeasurementList,
	getPowerGenerationRegisterStatusList,
	getPowerGenerationSourceList,
	getPowerGenerationStatusList,
	getPowerGenerationTypeList,
	getPropertyTypeList,
} from '../../../../helpers/establishment/backend_helper';

import { setRequireCloseModal } from '../../../../slices/powerGenerationSource/reducer';
import {
	sendNewEmailMessage,
	getEstablishment,
	getFeedingBarges,
	getFileTypeByCode,
	getUserByRoles,
	uploadFile,
	addNewNotification,
	addNewBulkNotification,
} from '../../../../slices/thunks';
import { useUser } from '../../../../Components/Hooks/useUserHooks';
import { sub } from 'date-fns';
import OInput from '../../../../Components/OElements/OInput';

// import sendMessage from '../../../../Components/Hooks/useNotificationHooks';

const PowerGenerationSourceForm = ({
	dataPowerGenerationSource,
	isEdit,
	handleToggleModal,
	isOpen,
	edit_environment = false,
}) => {
	const dispatch = useDispatch();
	const { user } = useUser();

	const [activeTab, setActiveTab] = useState('1');
	const [isLoading, setIsLoading] = useState(false);
	const [previousEstablishmentName, setPreviousEstablishmentName] =
		useState('');
	const [previousBargeName, setPreviousBargeName] = useState('');

	const [selectedEstablishmentType, setSelectedEstablishmentType] =
		useState(null);
	const [showFeedingBarges, setShowFeedingBarges] = useState(false);
	const [showEstablishment, setShowEstablishment] = useState(false);
	const [establishmentProductiveCycle, setEstablishmentProductiveCycle] =
		useState(null);
	const [associatedToFeedingBarges, setAssociatedToFeedingBarges] =
		useState(false);
	const [statusSelect, setStatusSelect] = useState(null);
	const [fuelTypeUnitMeasurement, setFuelTypeUnitMeasurement] = useState(null);

	const toggleTab = tab => {
		if (activeTab !== tab) {
			setActiveTab(tab);
		}
	};

	const selectorPowerGenerationSource = createSelector(
		state => state.PowerGenerationSource,
		result => ({
			isAdding: result.isAdding,
			isUpdating: result.isUpdating,
			requireCloseModal: result.requireCloseModal,
		})
	);
	const { isAdding, isUpdating, requireCloseModal } = useSelector(
		selectorPowerGenerationSource
	);

	const selectorUser = createSelector(
		state => state.Roles,
		result => ({
			users: result.itemUserRoleList,
		})
	);
	const { users } = useSelector(selectorUser);

	const selector = createSelector(
		state => state.FileManager,
		data => ({
			file_type: data?.file_type || null,
		})
	);
	const { file_type } = useSelector(selector);

	const selectorStatus = createSelector(
		state => state.PowerGenerationRegisterStatus,
		data => ({
			register_status: data.itemPowerGenerationRegisterStatus,
		})
	);
	const { register_status } = useSelector(selectorStatus);

	const selectorMessage = createSelector(
		state => state.Messages,
		data => ({
			messageDetail: data.messageDetail,
		})
	);

	const { messageDetail } = useSelector(selectorMessage);

	const documentTypes = ['DOI', 'CO'];
	const [fileTypes, setFileTypes] = useState({
		DOI: null,
		GD: null,
	});

	useEffect(() => {
		const fetchFileTypes = async () => {
			const results = {};
			for (const type of documentTypes) {
				const response = await dispatch(getFileTypeByCode(type));
				results[type] = response.payload; // Guardar la respuesta por tipo
			}
			setFileTypes(results);
		};

		fetchFileTypes();
	}, [dispatch]);

	// useEffect(() => {
	// 	dispatch(getPowerGenerationRegisterStatusCode('NRE'))
	// }, [dispatch]);

	const selectorFuelTank = createSelector(
		state => state.FuelTank,
		data => ({
			fuelTankList: data.fuelTankList,
			fuelTankListSelect: data.fuelTankListSelect,
		})
	);

	const { fuelTankList, fuelTankListSelect } = useSelector(selectorFuelTank);

	const selectorFeedingBarges = createSelector(
		state => state.FeedingBarges,
		holding => ({ itemFeedingBargesDetail: holding.itemFeedingBargesDetail })
	);
	const { itemFeedingBargesDetail } = useSelector(selectorFeedingBarges);

	const selectorEstablishment = createSelector(
		state => state.Establishments,
		holding => ({ itemEstablishmentDetail: holding.itemEstablishmentDetail })
	);
	const { itemEstablishmentDetail } = useSelector(selectorEstablishment);

	useEffect(() => {
		const roles = ['ADM', 'MAB', 'OPE', 'MNT'];
		const params = { roles: roles.join(',') };

		if (associatedToFeedingBarges) {
			const establishmentId = associatedToFeedingBarges
				? establishmentProductiveCycle || null // Fallback si no está inicializado
				: itemEstablishmentDetail?._id; // Validación segura con ?.

			if (establishmentId) {
				params['establishment_id'] = establishmentId;
			}
		}

		dispatch(getUserByRoles(params));
	}, [
		dataPowerGenerationSource,
		itemEstablishmentDetail,
		itemFeedingBargesDetail,
		associatedToFeedingBarges,
		establishmentProductiveCycle,
	]);

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			// Información básica
			name: dataPowerGenerationSource?.name || null,
			description: dataPowerGenerationSource?.description || null,
			brand: dataPowerGenerationSource?.brand || null,
			model: dataPowerGenerationSource?.model || null,
			serial_number: dataPowerGenerationSource?.serial_number || null,
			engine_number: dataPowerGenerationSource?.engine_number || null,
			engine_model: dataPowerGenerationSource?.engine_model || null,
			engine_brand: dataPowerGenerationSource?.engine_brand || null,
			power_generation_type_id:
				dataPowerGenerationSource?.power_generation_type_id || null,
			power_generation_status_id:
				dataPowerGenerationSource?.power_generation_status_id || null,
			property_type_id: dataPowerGenerationSource?.property_type_id || null,
			power_generation_register_status_id:
				dataPowerGenerationSource?.power_generation_register_status_id ||
				null,

			// Características técnicas
			output_power: dataPowerGenerationSource?.output_power || null,
			thermal_power: dataPowerGenerationSource?.thermal_power || null,
			maximum_power: dataPowerGenerationSource?.maximum_power || null,
			net_engine_power: dataPowerGenerationSource?.net_engine_power || null,
			fuel_type_id: dataPowerGenerationSource?.fuel_type_id || null,
			fuel_type_unit_measurement_id:
				dataPowerGenerationSource?.fuel_type_unit_measurement_id || null,
			nominal_consumption:
				dataPowerGenerationSource?.nominal_consumption || null,
			calculation_manual:
				dataPowerGenerationSource?.calculation_manual ?? true,
			volumetric_displacement_per_engine_cylinder:
				dataPowerGenerationSource?.volumetric_displacement_per_engine_cylinder ||
				null,
			maximum_engine_speed:
				dataPowerGenerationSource?.maximum_engine_speed || null,
			operating_mode: dataPowerGenerationSource?.operating_mode || null,
			useful_life_hours:
				dataPowerGenerationSource?.useful_life_hours || null,
			hour_meter: dataPowerGenerationSource?.hour_meter || null,

			// Fechas importantes
			year_of_manufacture:
				dataPowerGenerationSource?.year_of_manufacture || null,
			year_of_installation:
				dataPowerGenerationSource?.year_of_installation || null,
			date_of_initial_operation:
				dataPowerGenerationSource?.date_of_initial_operation || null,
			document_initial_operation:
				dataPowerGenerationSource?.document_initial_operation || null,
			document_initial_operation_file: null,
			date_of_first_declaration:
				dataPowerGenerationSource?.date_of_first_declaration || null,
			date_registration:
				dataPowerGenerationSource?.date_registration || null,
			date_certificate: dataPowerGenerationSource?.date_certificate || null,

			// Información de registro RFP
			rfp_code: dataPowerGenerationSource?.rfp_code || null,
			rfp_date_registration:
				dataPowerGenerationSource?.rfp_date_registration || null,

			// Documentación y certificados
			CCF8: dataPowerGenerationSource?.CCF8 || null,
			certificate_of_origin:
				dataPowerGenerationSource?.certificate_of_origin || null,
			certificate_of_origin_file: null,
			equipment_or_measurement_method:
				dataPowerGenerationSource?.equipment_or_measurement_method || null,
			temporal_resolution:
				dataPowerGenerationSource?.temporal_resolution || null,

			// Información de registro con la SEREMI
			seremi_registration_number:
				dataPowerGenerationSource?.seremi_registration_number || null,
			seremi_date_registration:
				dataPowerGenerationSource?.seremi_date_registration || null,

			// Identificación y estado de generación
			feeding_barges_id:
				dataPowerGenerationSource?.feeding_barges_id || null,
			establishment_id: dataPowerGenerationSource?.establishment_id || null,
			feeding_barges_name:
				dataPowerGenerationSource?.feeding_barges_name || null,
			establishment_name:
				dataPowerGenerationSource?.establishment_name || null,
			establishment_type_id:
				dataPowerGenerationSource?.establishment_type_id || null,
			fuel_tank_id: dataPowerGenerationSource?.fuel_tank_id || null,
		},
		validationSchema: Yup.object({
			// Información básica
			name: Yup.string().required('Campo requerido'),
			description: Yup.string().nullable(),
			brand: Yup.string().required('Campo requerido'),
			model: Yup.string().required('Campo requerido'),
			serial_number: Yup.string().required('Campo requerido'),
			engine_number: Yup.string().required('Campo requerido'),
			engine_model: Yup.string().required('Campo requerido'),
			engine_brand: Yup.string().required('Campo requerido'),
			power_generation_type_id: Yup.string().required('Campo requerido'),
			power_generation_status_id: Yup.string().required('Campo requerido'),
			property_type_id: Yup.string().required('Campo requerido'),
			power_generation_register_status_id: Yup.string().nullable(),

			// Características técnicas
			output_power: Yup.number()
				.positive('Debe ser un número positivo')
				.required('Campo requerido'),
			thermal_power: Yup.number()
				.positive('Debe ser un número positivo')
				.required('Campo requerido'),
			maximum_power: Yup.number()
				.positive('Debe ser un número positivo')
				.required('Campo requerido'),
			net_engine_power: Yup.number()
				.positive('Debe ser un número positivo')
				.required('Campo requerido'),
			fuel_type_id: Yup.string().required('Campo requerido'),
			fuel_type_unit_measurement_id:
				Yup.string().required('Campo requerido'),
			nominal_consumption: Yup.number()
				.positive('Debe ser un número positivo')
				.required('Campo requerido'),
			volumetric_displacement_per_engine_cylinder: Yup.number()
				.positive('Debe ser un número positivo')
				.required('Campo requerido'),
			maximum_engine_speed: Yup.number()
				.integer()
				.positive('Debe ser un número positivo')
				.required('Campo requerido'),
			operating_mode: Yup.string().nullable(),
			useful_life_hours: Yup.number()
				.integer()
				.positive('Debe ser un número positivo')
				.required('Campo requerido'),
			// hour_meter: Yup.number().integer().positive('Debe ser un número positivo').required('Campo requerido'),

			calculation_manual: Yup.boolean().required('Campo requerido'),

			hour_meter: Yup.lazy(() => {
				return isEdit
					? Yup.number().nullable()
					: Yup.number()
							.integer()
							.positive('Debe ser un número positivo')
							.required('Campo requerido');
			}),

			// Fechas importantes
			year_of_manufacture: Yup.number()
				.required('Campo requerido')
				.integer()
				.min(1900, 'No es un año válido')
				.max(new Date().getFullYear(), 'No es un año válido'),
			year_of_installation: Yup.number()
				.required('Campo requerido')
				.integer()
				.min(1900, 'No es un año válido')
				.max(new Date().getFullYear(), 'No es un año válido'),
			date_of_initial_operation: Yup.date().nullable(),
			document_initial_operation: Yup.string()
				.nullable()
				.test(
					'document-initial-operation-or-file',
					'Debe proporcionar el documento o un archivo.',
					function (value) {
						const {
							document_initial_operation_file,
							date_of_initial_operation,
						} = this.parent;
						if (
							!value &&
							!document_initial_operation_file &&
							date_of_initial_operation
						) {
							return false; // Si ambos están vacíos, falla la validación
						}
						return true; // Pasa la validación si al menos uno tiene valor
					}
				),
			document_initial_operation_file: Yup.mixed().nullable(),
			date_of_first_declaration: Yup.date().nullable(),
			date_registration: Yup.date().nullable(),
			date_certificate: Yup.date().nullable(),

			// Información de registro RFP
			rfp_code: Yup.string().nullable(),
			rfp_date_registration: Yup.date().nullable(),

			// Documentación y certificados
			CCF8: Yup.string().nullable(),
			// certificate_of_origin: Yup.string().nullable().test(
			// 	'certificate-of-origin-or-file',
			// 	'Debe proporcionar el documento o un archivo.',
			// 	function (value) {
			// 		const { certificate_of_origin_file } = this.parent;
			// 		if (!value && !certificate_of_origin_file) {
			// 			return false; // Si ambos están vacíos, falla la validación
			// 		}
			// 		return true; // Pasa la validación si al menos uno tiene valor
			// 	}
			// ),
			certificate_of_origin: Yup.mixed().nullable(),
			certificate_of_origin_file: Yup.mixed().nullable(),
			equipment_or_measurement_method: Yup.string().nullable(),
			temporal_resolution: Yup.string().nullable(),
			// Información de registro con la SEREMI
			seremi_registration_number: Yup.string().nullable(),
			seremi_date_registration: Yup.date().nullable(),

			// Identificación y estado de generación
			establishment_type_id: Yup.string().required('Campo requerido'),
			feeding_barges_id: Yup.lazy(() =>
				selectedEstablishmentType?.barges_needed
					? Yup.string().required('Campo requerido')
					: Yup.string().nullable()
			),
			establishment_id: Yup.lazy(() =>
				selectedEstablishmentType &&
				!selectedEstablishmentType.barges_needed
					? Yup.string().required('Campo requerido')
					: Yup.string().nullable()
			),
			fuel_tank_id: Yup.lazy(() => {
				return !statusSelect ||
					(statusSelect?.code &&
						['OPR', 'MNT', 'PRB', 'EMG'].includes(statusSelect.code))
					? Yup.string().required('Campo requerido')
					: Yup.string().nullable();
			}),
		}),
		onSubmit: async values => {
			setIsLoading(true);
			debugger;

			// try {
			// 	await validationSchema.validate(values, { abortEarly: false });
			// 	console.log("Validación exitosa");
			// } catch (err) {
			// 	console.log("Errores de Yup:", err);
			// 	setIsLoading(false);
			// }

			const powerGenerationSource = { ...values };

			if (associatedToFeedingBarges) {
				powerGenerationSource['establishment_id'] = null;
			} else {
				powerGenerationSource['feeding_barges_id'] = null;
			}

			let document_initial_operation_id = null;
			const folderPath =
				'/Operaciones/Fuentes de Generación de Energía/' +
				powerGenerationSource.serial_number;
			let changed = false;
			let new_source = false;

			if (values.document_initial_operation_file) {
				const formData = new FormData();
				formData.append('file', values.document_initial_operation_file);
				try {
					const responseUploadFile = await dispatch(
						uploadFile({
							file_path: folderPath,
							file_type_id: fileTypes.DOI._id,
							formData: formData,
						})
					).unwrap();
					document_initial_operation_id = responseUploadFile._id;
					powerGenerationSource['document_initial_operation'] =
						document_initial_operation_id;
				} catch (error) {
					setIsLoading(false);
					console.error('Error al subir archivo:', error);
				}
			}

			let certificate_of_origin_id = null;
			if (values.certificate_of_origin_file) {
				const formData = new FormData();
				formData.append('file', values.certificate_of_origin_file);
				try {
					const responseUploadFile = await dispatch(
						uploadFile({
							file_path: folderPath,
							file_type_id: fileTypes.CO._id,
							formData: formData,
						})
					).unwrap();
					certificate_of_origin_id = responseUploadFile._id;
					powerGenerationSource['certificate_of_origin'] =
						certificate_of_origin_id;
				} catch (error) {
					setIsLoading(false);
					console.error('Error al subir archivo:', error);
				}
			}

			let subject = 'Fuente de Generación de Energía';
			let notification_type = 'INF';
			let message = '';
			let body = `
					<div style="max-width: 600px; margin: auto; font-family: Arial, sans-serif; line-height: 1.6; color: #333; background-color: #f9f9f9; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
							<div style="text-align: center; margin-bottom: 20px;">
									<h1 style="color: COLOR_SECONDARY">Fuente de Generación de Energía</h1>
							</div>`;

			if (isEdit) {
				const isFeedingBargesChanged =
					dataPowerGenerationSource?.feeding_barges_id !==
					values.feeding_barges_id;
				const isEstablishmentChanged =
					dataPowerGenerationSource?.establishment_id !==
					values.establishment_id;

				if (isFeedingBargesChanged || isEstablishmentChanged) {
					const responseGetPowerGenerationRegisterStatusCode =
						await dispatch(
							getPowerGenerationRegisterStatusCode('PFR')
						).unwrap();
					powerGenerationSource['power_generation_register_status_id'] =
						responseGetPowerGenerationRegisterStatusCode._id;
				}

				powerGenerationSource._id = dataPowerGenerationSource._id;
				await dispatch(updatePowerGenerationSource(powerGenerationSource));

				if (isFeedingBargesChanged || isEstablishmentChanged) {
					changed = true;
					subject = 'Actualización en Fuente de Generación de Energía';

					body += `
							<p>Información de la Fuente de Generación de Energía</p>
							<table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Nombre:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.name.toUpperCase()}</td>
            </tr>
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Marca:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.brand.toUpperCase()}</td>
            </tr>
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Modelo:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.model.toUpperCase()}</td>
            </tr>
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Nro Serie:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.serial_number.toUpperCase()}</td>
            </tr>
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Nro Motor:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.engine_number.toUpperCase()}</td>
            </tr>
        </table>
				<p>Se realizaron los siguientes cambios</p>`;

					notification_type = 'ADV';
					message =
						'Se han realizado cambios en la fuente de generación de energía. Nombre: ' +
						values.name.toUpperCase() +
						', Marca: ' +
						values.brand.toUpperCase() +
						', Modelo: ' +
						values.model.toUpperCase() +
						', Nro Serie: ' +
						values.serial_number.toUpperCase() +
						', Nro Motor: ' +
						values.engine_number.toUpperCase() +
						'.';

					if (isFeedingBargesChanged && associatedToFeedingBarges) {
						body += `<table style="width: 100%; border-collapse: collapse; margin-top: 20px;"><tr>
												<td style="padding: 10px; border: 1px solid #ddd;"><b>Artefacto Naval:</b></td>
												<td style="padding: 10px; border: 1px solid #ddd;">${previousBargeName.toUpperCase()} → ${itemFeedingBargesDetail.name.toUpperCase()}</td>
										</tr></table>`;
						message +=
							' Artefacto Naval: ' +
							previousBargeName.toUpperCase() +
							' → ' +
							itemFeedingBargesDetail.name.toUpperCase() +
							'.';
					}
					if (isEstablishmentChanged && !associatedToFeedingBarges) {
						body += `<table style="width: 100%; border-collapse: collapse; margin-top: 20px;"><tr>
												<td style="padding: 10px; border: 1px solid #ddd;"><b>Establecimiento:</b></td>
												<td style="padding: 10px; border: 1px solid #ddd;">${previousEstablishmentName.toUpperCase()} → ${itemEstablishmentDetail.name.toUpperCase()}</td>
											</tr></table>`;
						message +=
							' Establecimiento: ' +
							previousEstablishmentName.toUpperCase() +
							' → ' +
							itemEstablishmentDetail.name.toUpperCase() +
							'.';
					}
				}
			} else {
				const responseGetPowerGenerationRegisterStatusCode = await dispatch(
					getPowerGenerationRegisterStatusCode('NRE')
				).unwrap();
				powerGenerationSource['power_generation_register_status_id'] =
					responseGetPowerGenerationRegisterStatusCode._id;
				await dispatch(addNewPowerGenerationSource(powerGenerationSource));

				new_source = true;
				subject = 'Nueva Fuente de Generación de Energía';

				body += `
							<p>Información de la nueva Fuente de Generación de Energía</p>
							<table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Nombre:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.name.toUpperCase()}</td>
            </tr>
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Marca:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.brand.toUpperCase()}</td>
            </tr>
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Modelo:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.model.toUpperCase()}</td>
            </tr>
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Nro Serie:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.serial_number.toUpperCase()}</td>
            </tr>
            <tr>
                <td style="padding: 10px; border: 1px solid #ddd;"><b>Nro Motor:</b></td>
                <td style="padding: 10px; border: 1px solid #ddd;">${values.engine_number.toUpperCase()}</td>
            </tr>`;
				message =
					'Se ha registrado una nueva fuente de generación de energía. Nombre: ' +
					values.name.toUpperCase() +
					', Marca: ' +
					values.brand.toUpperCase() +
					', Modelo: ' +
					values.model.toUpperCase() +
					', Nro Serie: ' +
					values.serial_number.toUpperCase() +
					', Nro Motor: ' +
					values.engine_number.toUpperCase() +
					'.';

				if (associatedToFeedingBarges) {
					body += `<tr>
											<td style="padding: 10px; border: 1px solid #ddd;"><b>Artefacto Naval:</b></td>
											<td style="padding: 10px; border: 1px solid #ddd;">${itemFeedingBargesDetail.name.toUpperCase()}</td>
									</tr></table>`;
					message +=
						' Artefacto Naval: ' +
						itemFeedingBargesDetail.name.toUpperCase() +
						'.';
				} else {
					body += `<tr>
											<td style="padding: 10px; border: 1px solid #ddd;"><b>Establecimiento:</b></td>
											<td style="padding: 10px; border: 1px solid #ddd;">${itemEstablishmentDetail.name.toUpperCase()}</td>
										</tr></table>`;
					message +=
						' Establecimiento: ' +
						itemEstablishmentDetail.name.toUpperCase() +
						'.';
				}
			}

			if (changed || new_source) {
				if (user?.full_name) {
					if (new_source) {
						body += `<p>Se ha registrado una nueva fuente de generación de energía por parte de ${user.full_name.toUpperCase()}</p>`;
						message +=
							' Agregado por ' + user.full_name.toUpperCase() + '.';
					} else {
						body += `<p>Se han realizado cambios en la fuente de generación de energía por parte de ${user.full_name.toUpperCase()}</p>`;
						message +=
							' Modificado por ' + user.full_name.toUpperCase() + '.';
					}
				}

				body += `<div style="margin-top: 30px; text-align: center; font-size: 0.7em;">
												<p>Si tienes alguna pregunta o necesitas asistencia, por favor <a href="mailto:ayuda@openlink.cl" style="color: COLOR_SECONDARY;">contáctanos</a>.</p>
												<p>Gracias. Equipo de Openlink</p>
										</div>
								</div>
						`;

				if (users.length > 0) {
					const uniqueEmails = [
						...new Set(users.map(user => user.email)),
					].join(',');
					const message_email = {
						to_email: uniqueEmails,
						subject: subject,
						body: body,
					};
					// Enviar el mensaje con dispatch
					dispatch(sendNewEmailMessage(message_email));

					const user_ids = [...new Set(users.map(user => user._id))]; // Sin .join(','), mantiene el array
					const message_notification = {
						title: subject,
						message: message,
						notification_priority_code: 'M',
						notification_type_code: notification_type,
						user_ids: user_ids,
					};
					dispatch(addNewBulkNotification(message_notification));
				}
			}

			dispatch(getPowerGenerationSourceList());
			setIsLoading(false);
		},
	});

	useEffect(() => {
		const fetchFeedingBarges = async () => {
			await Promise.all([
				dispatch(
					getFeedingBarges(dataPowerGenerationSource.feeding_barges_id)
				),
				dispatch(
					getEstablishment(dataPowerGenerationSource.establishment_id)
				),
			]);

			if (isEdit && dataPowerGenerationSource) {
				setPreviousEstablishmentName(
					dataPowerGenerationSource.establishment_name ||
						itemEstablishmentDetail?.name
				);
				setPreviousBargeName(
					dataPowerGenerationSource.feeding_barges_name ||
						itemFeedingBargesDetail?.name
				);
				validation.setFieldValue(
					'establishment_name',
					itemEstablishmentDetail?.name
				);
				validation.setFieldValue(
					'feeding_barges_name',
					itemFeedingBargesDetail?.name
				);
			}
		};
		fetchFeedingBarges();
	}, [isEdit, dataPowerGenerationSource, dispatch]);

	useEffect(() => {
		dispatch(getFeedingBarges(validation.values.feeding_barges_id));
	}, [validation.values.feeding_barges_id]);

	useEffect(() => {
		dispatch(getEstablishment(validation.values.establishment_id));
	}, [validation.values.establishment_id]);

	useEffect(() => {
		if (isOpen && requireCloseModal) {
			dispatch(setRequireCloseModal(false));
			handleToggleModal();
		}
	}, [requireCloseModal]);

	const handleFileChangeInitialOperation = event => {
		const file = event.currentTarget.files[0];
		validation.setFieldValue('document_initial_operation_file', file);
	};
	const handleFileChangeCertificateOfOrigin = event => {
		const file = event.currentTarget.files[0];
		validation.setFieldValue('certificate_of_origin_file', file);
	};

	const handleEstablishmentTypeSelect = optionSelected => {
		setSelectedEstablishmentType(optionSelected);
		if (optionSelected) {
			setAssociatedToFeedingBarges(optionSelected.barges_needed);
			if (optionSelected?.barges_needed) {
				setShowFeedingBarges(true);
				setShowEstablishment(false);
			} else {
				setShowFeedingBarges(false);
				setShowEstablishment(true);
			}
		}
	};

	const handleFeedingBargesSelect = optionSelected => {
		if (optionSelected) {
			const establishmentId = optionSelected.establishment?._id || null;
			setEstablishmentProductiveCycle(establishmentId);
		}
	};

	const handleStatusSelect = selected => {
		setStatusSelect(selected);
	};

	const handleFuelTypeUnitMeasurement = selected => {
		setFuelTypeUnitMeasurement(selected);
	};

	return (
		<Form
			onSubmit={e => {
				e.preventDefault();
				if (Object.keys(validation.errors).length != 0) {
					toast.error(
						'Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.',
						{ autoClose: 4000 }
					);
				}
				validation.handleSubmit();
				return false;
			}}
		>
			<div className='modal-content border-0 mt-3'>
				<Nav className='nav-tabs nav-tabs-custom nav-success p-2 pb-0 bg-light'>
					<NavItem>
						<NavLink
							href='#'
							className={classnames({ active: activeTab === '1' })}
							onClick={() => toggleTab('1')}
						>
							Información General
						</NavLink>
					</NavItem>
					<NavItem>
						<NavLink
							href='#'
							className={classnames({ active: activeTab === '2' })}
							onClick={() => toggleTab('2')}
						>
							Asignación
						</NavLink>
					</NavItem>
					<NavItem>
						<NavLink
							href='#'
							className={classnames({ active: activeTab === '3' })}
							onClick={() => toggleTab('3')}
						>
							Características Técnicas
						</NavLink>
					</NavItem>
					<NavItem>
						<NavLink
							href='#'
							className={classnames({ active: activeTab === '4' })}
							onClick={() => toggleTab('4')}
						>
							Documentación y Certificados
						</NavLink>
					</NavItem>
				</Nav>
			</div>
			<ModalBody className='modal-body'>
				<TabContent activeTab={activeTab}>
					<TabPane tabId='1'>
						<Row className='g-3'>
							<Col lg={3}>
								<OInput
									name='name'
									label='Nombre'
									type='text'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='serial_number'
									label='Número de Serie'
									type='text'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='brand'
									label='Marca'
									type='text'
									placeholder='Ingrese la marca'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='model'
									label='Modelo'
									type='text'
									placeholder='Ingrese la marca'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='engine_number'
									label='Número de Motor'
									type='text'
									validation={validation}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='engine_model'
									label='Modelo de Motor'
									type='text'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='engine_brand'
									label='Marca de Motor'
									type='text'
									placeholder='Ingrese la marca del motor'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='useful_life_hours'
									label='Horas de Vida Útil'
									type='text'
									placeholder='Ingrese las horas de vida útil'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='year_of_manufacture'
									label='Año de Fabricación'
									type='text'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='year_of_installation'
									label='Año de Instalación'
									type='text'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OSelect
									key='property_type_id'
									label='Tipo de Propiedad'
									id='property_type_id'
									name='property_type_id'
									disabled={edit_environment}
									isRequired={true}
									value={
										dataPowerGenerationSource?.property_type_id || ''
									}
									onChange={validation.handleChange}
									requestApi={() => getPropertyTypeList({})}
									validation={validation}
								/>
							</Col>
							<Col lg={3}>
								<OSelect
									key='power_generation_status_id'
									label='Estado de Generación de Energía'
									id='power_generation_status_id'
									name='power_generation_status_id'
									disabled={edit_environment}
									isRequired={true}
									value={
										dataPowerGenerationSource?.power_generation_status_id ||
										''
									}
									onChange={validation.handleChange}
									onSelect={handleStatusSelect}
									requestApi={() => getPowerGenerationStatusList({})}
									validation={validation}
								/>
							</Col>
							<Col lg={3}>
								<OSelect
									key='power_generation_type_id'
									label='Tipo de Generación de Energía'
									id='power_generation_type_id'
									name='power_generation_type_id'
									isRequired={true}
									value={
										dataPowerGenerationSource?.power_generation_type_id ||
										''
									}
									onChange={validation.handleChange}
									requestApi={() => getPowerGenerationTypeList({})}
									validation={validation}
									disabled={edit_environment}
								/>
							</Col>
							<Col lg={3}>
								<OSelect
									key='fuel_type_id'
									label='Tipo de Combustible'
									id='fuel_type_id'
									name='fuel_type_id'
									isRequired={true}
									value={dataPowerGenerationSource?.fuel_type_id || ''}
									onChange={validation.handleChange}
									requestApi={() => getFuelTypeList({})}
									validation={validation}
									disabled={edit_environment}
								/>
							</Col>
							<Col lg={3}>
								<OSelect
									key='fuel_type_unit_measurement_id'
									label='Unidad Medida de Combustible'
									id='fuel_type_unit_measurement_id'
									name='fuel_type_unit_measurement_id'
									isRequired={true}
									value={
										dataPowerGenerationSource?.fuel_type_unit_measurement_id ||
										''
									}
									onChange={validation.handleChange}
									onSelect={handleFuelTypeUnitMeasurement}
									requestApi={() => getFuelTypeUnitMeasurementList({})}
									validation={validation}
									disabled={edit_environment}
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='nominal_consumption'
									label={`Consumo Nominal (${
										fuelTypeUnitMeasurement?.name ?? '-'
									})`}
									type='number'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<div className='d-flex align-items-center'>
									<Label
										className='form-check-label me-2'
										htmlFor='calculation_manual'
									>
										Cálculo de Consumo
									</Label>
									<div className='form-check form-switch'>
										<input
											type='checkbox'
											className='form-check-input'
											id='calculation_manual'
											name='calculation_manual'
											checked={validation.values.calculation_manual}
											onChange={e =>
												validation.setFieldValue(
													'calculation_manual',
													e.target.checked
												)
											}
										/>
									</div>
									<span className='ms-2'>
										{validation.values.calculation_manual
											? 'Manual'
											: 'Automático'}
									</span>
								</div>
							</Col>
							<Col lg={12}>
								<OInput
									name='description'
									label='Descripción'
									type='textarea'
									placeholder='Ingrese la descripción'
									validation={validation}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
						</Row>
					</TabPane>

					<TabPane tabId='2'>
						<Row>
							<Col lg={3}>
								<OSelect
									label='Tipo de Establecimiento'
									id='establishment_type_id'
									name='establishment_type_id'
									isRequired={true}
									value={
										dataPowerGenerationSource?.establishment_type_id ||
										''
									}
									onSelect={handleEstablishmentTypeSelect}
									requestApi={() => getEstablishmentTypeList({})}
									disabled={edit_environment}
									validation={validation}
								/>
							</Col>

							{showEstablishment && (
								<Col lg={3}>
									<OSelect
										key={
											selectedEstablishmentType?._id ||
											'establishment_id'
										}
										label='Establecimiento'
										id='establishment_id'
										name='establishment_id'
										isRequired={true}
										value={
											dataPowerGenerationSource?.establishment_id ||
											''
										}
										onChange={validation.handleChange}
										requestApi={() =>
											getEstablishmentList({
												establishment_type_id:
													selectedEstablishmentType._id,
											})
										}
										disabled={edit_environment}
										validation={validation}
									/>
								</Col>
							)}

							{showFeedingBarges && (
								<Col lg={3}>
									<OSelect
										label='Artefacto Naval'
										id='feeding_barges_id'
										name='feeding_barges_id'
										isRequired={true}
										value={
											dataPowerGenerationSource?.feeding_barges_id ||
											''
										}
										onSelect={handleFeedingBargesSelect}
										onChange={validation.handleChange}
										requestApi={() => getFeedingBargesList({})}
										disabled={edit_environment}
										validation={validation}
									/>
								</Col>
							)}

							{(showFeedingBarges || showEstablishment) && (
								<Col lg={3}>
									<OSelect
										key={`fuel_tank_name_${
											validation.values.fuel_type_id || ''
										}_${validation.values.establishment_id || ''}_${
											validation.values.feeding_barges_id || ''
										}`}
										label='Estanque de Combustible (principal)'
										id='fuel_tank_id'
										name='fuel_tank_id'
										isRequired={true}
										value={
											dataPowerGenerationSource?.fuel_tank_id || ''
										}
										requestApi={() => {
											if (
												validation.values.fuel_type_id &&
												(validation.values.establishment_id ||
													validation.values.feeding_barges_id)
											) {
												const params = {
													fuel_type_id:
														validation.values.fuel_type_id,
													...(showEstablishment &&
													validation.values.establishment_id
														? {
																establishment_id:
																	validation.values
																		.establishment_id,
														  }
														: {}),
													...(showFeedingBarges &&
													validation.values.feeding_barges_id
														? {
																feeding_barges_id:
																	validation.values
																		.feeding_barges_id,
														  }
														: {}),
												};
												return getFuelTankListByEstablishment(
													params
												); // Asegúrate de que esta función retorna la lista adecuada.
											} else {
												return null;
											}
										}}
										disabled={edit_environment}
										validation={validation}
									/>
								</Col>
							)}
						</Row>
					</TabPane>

					<TabPane tabId='3'>
						<Row className='g-3'>
							<Col lg={3}>
								<OInput
									name='output_power'
									label='Potencia de Salida (kW)'
									type='number'
									validation={validation}
									disabled={edit_environment}
									placeholder='Ingrese la potencia de salida (kW)'
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='thermal_power'
									label='Potencia Térmica (kW)'
									placeholder='Ingrese la potencia térmica (kW)'
									type='number'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='volumetric_displacement_per_engine_cylinder'
									label='Desplazamiento Volumétrico por Cilindro'
									type='number'
									validation={validation}
									disabled={edit_environment}
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='maximum_engine_speed'
									label='Velocidad Máxima del Motor (rpm)'
									type='number'
									validation={validation}
									disabled={edit_environment}
									placeholder='Ingrese la velocidad máxima del motor (rpm)'
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='operating_mode'
									label='Modo de Operación'
									type='text'
									validation={validation}
									disabled={edit_environment}
									isRequired={false}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='equipment_or_measurement_method'
									label='Equipo o Método de Medición'
									type='text'
									validation={validation}
									disabled={edit_environment}
									isRequired={false}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='maximum_power'
									label='Potencia Máxima (kW)'
									type='number'
									validation={validation}
									disabled={edit_environment}
									placeholder='Ingrese la potencia máxima (kW)'
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							<Col lg={3}>
								<OInput
									name='net_engine_power'
									label='Potencia Neta Motor (kW)'
									type='number'
									validation={validation}
									disabled={edit_environment}
									placeholder='Ingrese la potencia neta motor (kW)'
									isRequired={true}
									classNameLabel='form-label'
									classNameInput='form-control'
								/>
							</Col>
							{!isEdit && (
								<Col lg={3}>
									<OInput
										name='hour_meter'
										label='Horómetro'
										type='number'
										validation={validation}
										disabled={edit_environment}
										isRequired={true}
										classNameLabel='form-label'
										classNameInput='form-control'
									/>
								</Col>
							)}
						</Row>
					</TabPane>

					<TabPane tabId='4'>
						<Row className='g-3'>
							<OHr mt={true} text={'Operacion inicial'} />

							<Col lg={3}>
								<DatesProvider
									settings={{
										locale: 'es-mx',
										firstDayOfWeek: 1,
										weekendDays: [0],
										timezone: 'UTC',
									}}
								>
									<DatePickerInput
										label='Fecha de Operación Inicial'
										id='date_of_initial_operation'
										name='date_of_initial_operation'
										placeholder='Seleccione una fecha'
										value={
											validation.values.date_of_initial_operation
												? new Date(
														validation.values.date_of_initial_operation
												  )
												: null
										}
										onChange={date =>
											validation.setFieldValue(
												'date_of_initial_operation',
												date
											)
										}
										onBlur={validation.handleBlur}
										valueFormat='DD-MM-YYYY' // Ajusta según el formato que necesitas para mostrar
										error={
											validation.touched[
												'date_of_initial_operation'
											] &&
											validation.errors['date_of_initial_operation']
												? validation.errors[
														'date_of_initial_operation'
												  ]
												: ''
										}
										clearable={true}
									/>
								</DatesProvider>
							</Col>

							<Col lg={6}>
								<Label for='document_initial_operation'>
									Documento Operación Inicial
								</Label>
								<Input
									name='document_initial_operation'
									id='document_initial_operation'
									className='form-control'
									placeholder='Ingrese el documento'
									type='file'
									validate={{ required: { value: true } }}
									onChange={handleFileChangeInitialOperation}
									onBlur={validation.handleBlur}
									invalid={
										validation.touched.document_initial_operation &&
										validation.errors.document_initial_operation
											? true
											: false
									}
								/>
								{validation.touched.document_initial_operation &&
								validation.errors.document_initial_operation ? (
									<FormFeedback type='invalid'>
										{validation.errors.document_initial_operation}
									</FormFeedback>
								) : null}
							</Col>

							<OHr mt={true} text={'Inscripción'} />

							<Col lg={3}>
								<OSelect
									key='power_generation_register_status_id'
									label='Estado del Registro'
									id='power_generation_register_status_id'
									name='power_generation_register_status_id'
									value={
										dataPowerGenerationSource?.power_generation_register_status_id ||
										''
									}
									onChange={validation.handleChange}
									requestApi={() =>
										getPowerGenerationRegisterStatusList({})
									}
									validation={validation}
								/>
							</Col>

							<Col lg={3}>
								<Label for='rfp_code' className='form-label'>
									Código RFP
								</Label>
								<Input
									name='rfp_code'
									id='rfp_code'
									type='text'
									placeholder='Ingrese el código RFP'
									{...(validation.getFieldProps('rfp_code') || {
										value: '',
									})}
									invalid={
										!!validation.errors.rfp_code &&
										validation.touched.rfp_code
									}
								/>
								<FormFeedback>
									{validation.errors.rfp_code}
								</FormFeedback>
							</Col>

							{/* 
							<Col lg={3} >
								<DatesProvider settings={{ locale: 'es-mx', firstDayOfWeek: 1, weekendDays: [0], timezone: 'UTC' }}>
									<DatePickerInput
										label='Fecha Inscripción'
										id='date_registration'
										name='date_registration'
										placeholder='Seleccione una fecha'
										value={
											validation.values.date_registration
												? new Date(validation.values.date_registration)
												: null
										}
										onChange={(date) => validation.setFieldValue('date_registration', date)}
										onBlur={validation.handleBlur}
										valueFormat='DD-MM-YYYY' // Ajusta según el formato que necesitas para mostrar
										error={
											validation.touched['date_registration'] && validation.errors['date_registration']
												? validation.errors['date_registration']
												: ''
										}
										clearable={true}
									/>
								</DatesProvider>
							</Col> */}

							<Col lg={3}>
								<DatesProvider
									settings={{ locale: 'es-mx', firstDayOfWeek: 1 }}
								>
									<DatePickerInput
										label='Fecha de Registro RFP'
										id='rfp_date_registration'
										name='rfp_date_registration'
										placeholder='Seleccione una fecha'
										value={
											validation.values.rfp_date_registration
												? new Date(
														validation.values.rfp_date_registration
												  )
												: null
										}
										onChange={date =>
											validation.setFieldValue(
												'rfp_date_registration',
												date
											)
										}
										onBlur={validation.handleBlur}
										valueFormat='DD-MM-YYYY'
										error={
											validation.touched['rfp_date_registration'] &&
											validation.errors['rfp_date_registration']
												? validation.errors['rfp_date_registration']
												: ''
										}
										clearable={true}
									/>
								</DatesProvider>
							</Col>
							<Col lg={3}>
								<Label for='CCF8' className='form-label'>
									Código CCF8
								</Label>
								<Input
									name='CCF8'
									id='CCF8'
									type='text'
									placeholder='Ingrese el código CCF8'
									{...(validation.getFieldProps('CCF8') || {
										value: '',
									})}
									invalid={
										!!validation.errors.CCF8 &&
										validation.touched.CCF8
									}
								/>
								<FormFeedback>{validation.errors.CCF8}</FormFeedback>
							</Col>
							<Col lg={3}>
								<DatesProvider
									settings={{
										locale: 'es-mx',
										firstDayOfWeek: 1,
										weekendDays: [0],
										timezone: 'UTC',
									}}
								>
									<DatePickerInput
										label='Fecha Primera Declaración'
										id='date_of_first_declaration'
										name='date_of_first_declaration'
										placeholder='Seleccione una fecha'
										value={
											validation.values.date_of_first_declaration
												? new Date(
														validation.values.date_of_first_declaration
												  )
												: null
										}
										onChange={date =>
											validation.setFieldValue(
												'date_of_first_declaration',
												date
											)
										}
										onBlur={validation.handleBlur}
										valueFormat='DD-MM-YYYY' // Ajusta según el formato que necesitas para mostrar
										error={
											validation.touched[
												'date_of_first_declaration'
											] &&
											validation.errors['date_of_first_declaration']
												? validation.errors[
														'date_of_first_declaration'
												  ]
												: ''
										}
										clearable={true}
									/>
								</DatesProvider>
							</Col>

							<OHr mt={true} text={'Certificación'} />

							<Col lg={3}>
								<DatesProvider
									settings={{
										locale: 'es-mx',
										firstDayOfWeek: 1,
										weekendDays: [0],
										timezone: 'UTC',
									}}
								>
									<DatePickerInput
										label='Fecha del Certificado'
										id='date_certificate'
										name='date_certificate'
										placeholder='Seleccione una fecha'
										value={
											validation.values.date_certificate
												? new Date(
														validation.values.date_certificate
												  )
												: null
										}
										onChange={date =>
											validation.setFieldValue(
												'date_certificate',
												date
											)
										}
										onBlur={validation.handleBlur}
										valueFormat='DD-MM-YYYY' // Ajusta según el formato que necesitas para mostrar
										error={
											validation.touched['date_certificate'] &&
											validation.errors['date_certificate']
												? validation.errors['date_certificate']
												: ''
										}
										clearable={true}
									/>
								</DatesProvider>
							</Col>

							<Col lg={6}>
								<Label for='certificate_of_origin'>
									Certificado de Origen
								</Label>
								<Input
									name='certificate_of_origin'
									id='certificate_of_origin'
									className='form-control'
									placeholder='Ingrese el documento'
									type='file'
									validate={{ required: { value: true } }}
									onChange={handleFileChangeCertificateOfOrigin}
									onBlur={validation.handleBlur}
									invalid={
										validation.touched.certificate_of_origin &&
										validation.errors.certificate_of_origin
											? true
											: false
									}
								/>
								{validation.touched.certificate_of_origin &&
								validation.errors.certificate_of_origin ? (
									<FormFeedback type='invalid'>
										{validation.errors.certificate_of_origin}
									</FormFeedback>
								) : null}
							</Col>

							<OHr mt={true} text={'SEREMI'} />
							<Col lg={3}>
								<Label
									for='seremi_registration_number'
									className='form-label'
								>
									Número de Registro SEREMI
								</Label>
								<Input
									name='seremi_registration_number'
									id='seremi_registration_number'
									type='text'
									placeholder='Ingrese el número de registro'
									{...(validation.getFieldProps(
										'seremi_registration_number'
									) || { value: '' })}
									invalid={
										!!validation.errors.seremi_registration_number &&
										validation.touched.seremi_registration_number
									}
								/>
								<FormFeedback>
									{validation.errors.seremi_registration_number}
								</FormFeedback>
							</Col>
							<Col lg={3}>
								<DatesProvider
									settings={{ locale: 'es-mx', firstDayOfWeek: 1 }}
								>
									<DatePickerInput
										label='Fecha de Registro SEREMI'
										id='seremi_date_registration'
										name='seremi_date_registration'
										placeholder='Seleccione una fecha'
										value={
											validation.values.seremi_date_registration
												? new Date(
														validation.values.seremi_date_registration
												  )
												: null
										}
										onChange={date =>
											validation.setFieldValue(
												'seremi_date_registration',
												date
											)
										}
										onBlur={validation.handleBlur}
										valueFormat='DD-MM-YYYY'
										error={
											validation.touched[
												'seremi_date_registration'
											] &&
											validation.errors['seremi_date_registration']
												? validation.errors[
														'seremi_date_registration'
												  ]
												: ''
										}
										clearable={true}
									/>
								</DatesProvider>
							</Col>
						</Row>
					</TabPane>
				</TabContent>
			</ModalBody>

			<div className='modal-footer'>
				<Button
					type='button'
					onClick={handleToggleModal}
					className='btn-danger btn-label'
				>
					<i className='icon-x label-icon'></i>Cancelar
				</Button>
				<button
					type='submit'
					className='btn btn-label btn-primary'
					id='add-btn'
					disabled={isLoading}
				>
					{isLoading ? (
						<i className='label-icon'>
							<Spinner size='sm' />
						</i>
					) : (
						<i className='ri-save-3-line label-icon'></i>
					)}
					{isEdit ? 'Editar' : 'Crear'} Fuente de Generación de Energía
				</button>
			</div>
		</Form>
	);
};

export default PowerGenerationSourceForm;
